import moment from "moment";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import useSWR from "swr";
import { ImageAttributePreview } from "../../../../components/AttributeGroupFormGenerator/ImageAttributePreview";
import { useAuthContext } from "../../../../components/Auth";
import { ErrorPlaceholder } from "../../../../components/Error";
import {
  ImagePlaceHolderIcon,
  LoadingIcon,
} from "../../../../components/Icons/Icons";
import { ToggleSwitchV2 } from "../../../../components/ToggleSwitch/ToggleSwitch";
import { endpoints } from "../../../../endpoints";
import { TwoColumGrid } from "../../../../layout/portalPageLayout";
import type {
  Assets,
  AssetsSummary,
  AttributeSchema,
  AttributeValue,
  ImageAttributeValue,
  IproductReferenceCollectionItem,
} from "../../../../types/types.PIM";
import { useRoutePath } from "../../../../util/Routing";
import { useStoreState } from "../../../../util/util";
import { LinkAttributeValueSchema } from "../../../../util/zod.util";
import type { OptionType } from "../../../../types/types";

const InnerContentText = styled.p`
  color: ${({ theme }) => theme.primaryTextColor};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  margin: 0;
`;

const UL = styled.ul`
  padding-left: 8px;
  margin: 5px 0 10px;
  margin-left: 13px;
  li {
    font-size: ${({ theme }) => theme.fontSizes.medium};
    line-height: 1.33;
    padding-bottom: 5px;
    &::marker {
      font-size: ${({ theme }) => theme.fontSizes.regular};
    }
    span {
      margin-left: -2px;
      position: relative;
    }
  }
`;

const MultilineWrapper = styled.div<{ line_clamp: number }>`
  p {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${({ line_clamp }) => line_clamp};
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
  }
`;

const LoadingIconContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
`;

const renderIconWithText = (
  value: AttributeValue,
  size: "small" | "medium" | "large"
) => {
  const imageSize = size === "small" ? 32 : size === "medium" ? 64 : 128;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: size === "large" ? "column" : "row",
        alignItems: size === "small" || size === "medium" ? "center" : "unset",
        justifyContent: "flex-start",
      }}
    >
      {value.enum_variant?.thumbnail ? (
        <img
          src={value.enum_variant?.thumbnail}
          alt="attribute-icon"
          width={imageSize}
          height={imageSize}
          style={{
            marginRight: size !== "large" ? "10px" : "0",
            marginBottom: size === "large" ? "10px" : "0",
            borderRadius: "3px",
          }}
        />
      ) : (
        <div
          style={{
            marginRight: size !== "large" ? "10px" : "0",
            marginBottom: size === "large" ? "10px" : "0",
          }}
        >
          <ImagePlaceHolderIcon width={imageSize} height={imageSize} />
        </div>
      )}
      <div>{value.enum_variant?.variant}</div>
    </div>
  );
};

const ImageAttributeDisplay = ({
  attribute,
  isPortfolio,
}: {
  attribute: AttributeSchema;
  isPortfolio: boolean;
}) => {
  const { tenant_id } = useStoreState();
  const { t } = useTranslation();

  const { data: asset, error: asset_error } = useSWR<AssetsSummary>(
    attribute.values?.length
      ? endpoints.v2_tenants_id_pim_assets_id_summary(
          tenant_id,
          (attribute.values[0].value as ImageAttributeValue).asset_id
        )
      : null
  );

  return (
    <>
      {!asset_error && !asset && (
        <LoadingIconContainer>
          <LoadingIcon width={24} height={24} />
        </LoadingIconContainer>
      )}
      {asset_error && <ErrorPlaceholder message={t("Could not load image")} />}
      {asset && (
        <ImageAttributePreview
          attribute={attribute}
          asset={asset as Assets}
          is_portfolio_page={isPortfolio}
        />
      )}
    </>
  );
};

export const AttributeValueDisplay = ({
  attribute,
  isPortfolio,
  max_line_clamp,
}: {
  attribute: AttributeSchema;
  isPortfolio: boolean;
  max_line_clamp?: number;
}) => {
  const { storePath, adminPath } = useRoutePath();
  const { t } = useTranslation();
  const { roleIsSellerAdmin, user } = useAuthContext();

  if (attribute.is_not_applicable) {
    return <InnerContentText>--</InnerContentText>;
  }

  switch (attribute.input_type) {
    case "single_select":
      if (attribute.icons_enabled && attribute.values?.length) {
        return renderIconWithText(
          attribute.values[0],
          attribute.size ?? "medium"
        );
      }
      return (
        <InnerContentText>
          {attribute.values?.length && attribute.values[0].value !== " "
            ? (attribute.values[0].value as string)
            : "--"}
        </InnerContentText>
      );
    case "numeric":
      return (
        <InnerContentText>
          {attribute.values?.length && attribute.values[0].value !== " "
            ? (attribute.values[0].value as string)
            : "--"}
        </InnerContentText>
      );
    case "date":
      return (
        <InnerContentText>
          {attribute.values?.length && attribute.values[0].value !== " "
            ? moment(attribute.values[0].value as string)?.format(
                "MMM DD, YYYY"
              )
            : "--"}
        </InnerContentText>
      );
    case "form_field":
      return (
        <InnerContentText>
          {attribute.values?.length && attribute.values[0].value !== " "
            ? (attribute.values[0].value as string)
            : "--"}
        </InnerContentText>
      );
    case "multiline_entry":
      return (
        <>
          {attribute.values?.length ? (
            <>
              {max_line_clamp ? (
                <MultilineWrapper
                  line_clamp={max_line_clamp}
                  dangerouslySetInnerHTML={{
                    __html:
                      `<div class="ql-editor">${
                        attribute.values[0].value as string
                      }</div>` || "--",
                  }}
                />
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      `<div class="ql-editor">${
                        attribute.values[0].value as string
                      }</div>` || "--",
                  }}
                />
              )}
            </>
          ) : (
            <InnerContentText>--</InnerContentText>
          )}
        </>
      );
    case "toggle":
      return (
        <>
          <ToggleSwitchV2
            label={attribute.display_name ? t([attribute.display_name]) : ""}
            name={attribute.name}
            checked={
              attribute.values?.length
                ? (attribute.values[0].value as boolean)
                : false
            }
            key={attribute.id}
            disabled={true}
          />
        </>
      );
    case "multi_select":
      const size = attribute.size;
      const imageSize = size === "small" ? 32 : size === "medium" ? 64 : 128;

      const itemWidth = size === "large" ? imageSize : `calc(33.33% - 10px)`;

      if (attribute.values?.length && !attribute.icons_enabled) {
        return (
          <TwoColumGrid>
            <UL>
              {attribute.values.map((value) => {
                return (
                  <li key={value.id}>
                    <span>{value.enum_variant?.variant}</span>
                  </li>
                );
              })}
            </UL>
          </TwoColumGrid>
        );
      }

      return attribute.values?.length && attribute.icons_enabled ? (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: "15px",
            justifyContent: "flex-start",
          }}
        >
          {attribute.values.map((value) => (
            <div key={value.id} style={{ width: itemWidth }}>
              {renderIconWithText(value, size)}
            </div>
          ))}
        </div>
      ) : (
        <InnerContentText>--</InnerContentText>
      );
    case "link":
      const parsed = LinkAttributeValueSchema.safeParse(
        attribute.values?.[0]?.value
      );

      if (parsed.success && parsed.data.url) {
        return (
          <InnerContentText>
            <a href={parsed.data.url} target={"_blank"} rel="noreferrer">
              {parsed.data.display_text}
            </a>
          </InnerContentText>
        );
      } else if (
        parsed.success &&
        !parsed.data.url &&
        parsed.data.display_text !== ""
      ) {
        return <InnerContentText>{parsed.data.display_text}</InnerContentText>;
      } else return <InnerContentText>--</InnerContentText>;
    case "product_reference":
      return attribute.values?.length ? (
        <UL>
          {attribute.values.map((value) => {
            const tenantHasAccess =
              value?.product_ref?.visibility_tenants?.find(
                (tenant) => tenant?.id === user?.tenant_id
              )?.id;
            return (
              <li key={value.id}>
                {(value?.product_ref?.status === "published" &&
                  value?.product_ref?.is_visible_on_storefronts &&
                  (value?.product_ref?.is_accessible || tenantHasAccess)) ||
                roleIsSellerAdmin ||
                (value?.product_ref?.status === "published" &&
                  value?.product_ref?.is_accessible &&
                  user) ||
                (value?.product_ref?.status === "published" &&
                  tenantHasAccess) ? (
                  <Link
                    to={
                      isPortfolio
                        ? `${storePath}/product/${value?.product_ref?.slug}`
                        : `${adminPath}/pim/products/${
                            value?.product_ref?.product_number ??
                            value?.product_ref?.id
                          }`
                    }
                  >
                    {value.product_ref?.name}
                  </Link>
                ) : (
                  <>
                    <span
                      data-tip={
                        value?.product_ref?.status !== "published"
                          ? t("Product informations is unavailable to view")
                          : t("Product informations is not accessible")
                      }
                      data-for={value.id}
                    >
                      {value.product_ref?.name}
                    </span>
                    <ReactTooltip
                      delayHide={200}
                      id={value.id}
                      effect="solid"
                    />
                  </>
                )}
              </li>
            );
          })}
        </UL>
      ) : (
        <InnerContentText>--</InnerContentText>
      );
    case "image":
      return attribute.values?.length ? (
        <ImageAttributeDisplay
          attribute={attribute}
          isPortfolio={isPortfolio}
        />
      ) : (
        <InnerContentText>--</InnerContentText>
      );
    default:
      return (
        <InnerContentText>
          {attribute.values?.length
            ? (attribute.values[0].value as string)
            : "--"}
        </InnerContentText>
      );
  }
};

export interface ProductGroupFormValues {
  [key: string]:
    | OptionType<string>
    | string
    | string[]
    | boolean
    | number
    | { display_text: string; url: string }
    | ImageAttributeValue
    | OptionType<string>[];
}

export const processAttributeFormValue = (
  value: Exclude<
    ProductGroupFormValues[string],
    OptionType<string>[] | string[]
  >,
  input_type: AttributeSchema["input_type"]
) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  switch (input_type) {
    case "date":
      return moment(value as string).format("MMM DD, YYYY");
    case "single_select":
    case "product_reference":
      return (value as OptionType<string>).value as string;
    case "link": {
      const { display_text, url } = value as {
        display_text: string;
        url: string;
      };
      return display_text === "" && url === ""
        ? null
        : (value as { display_text: string; url: string });
    }
    case "toggle":
    case "checkbox":
      return JSON.stringify(value as boolean);
    default:
      return value as string;
  }
};

export const isExistingAttributeFormValue = (
  value: Exclude<
    ProductGroupFormValues[string],
    OptionType<string>[] | string[]
  >,
  existingValue: AttributeValue["value"],
  input_type: AttributeSchema["input_type"]
) => {
  if (
    existingValue === null ||
    existingValue === undefined ||
    existingValue === ""
  ) {
    return false;
  }

  switch (input_type) {
    case "link":
      const existingLinkValue = existingValue as {
        url: string;
        display_text: string;
      };
      return (
        (value as { url: string; display_text: string }).url ===
          existingLinkValue.url &&
        (value as { url: string; display_text: string }).display_text ===
          existingLinkValue.display_text
      );
    case "image":
      const { asset_id, caption, image_dimensions } =
        value as ImageAttributeValue;
      const {
        asset_id: existing_asset_id,
        caption: existing_caption,
        image_dimensions: existing_image_dimensions,
      } = existingValue as ImageAttributeValue;
      return (
        asset_id === existing_asset_id &&
        caption?.trim() === existing_caption?.trim() &&
        image_dimensions === existing_image_dimensions
      );
    case "toggle":
    case "checkbox":
      return typeof value === "string"
        ? (value === "true") === (existingValue as boolean)
        : value === (existingValue as boolean);
    case "date":
      return value === moment(existingValue as string).format("MMM DD, YYYY");
    case "product_reference":
      return (value as OptionType)?.value
        ? (value as OptionType).value ===
            (existingValue as IproductReferenceCollectionItem).id
        : value === (existingValue as IproductReferenceCollectionItem).id;
    default:
      return value === existingValue;
  }
};
